import { Button, Popconfirm, Tag } from 'antd';
import clsx from 'clsx';
import { FileX, ShieldAlert, X } from 'lucide-react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonFields } from '../../../../../shared/components/SkeletonFields';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import type { ZoneKind } from '../../../../reference-data/reference-data.api';
import { useZonePanel } from '../hooks/use-zone-panel';
import { ZoneDrawControls } from './ZoneDrawControls';
import { ZonePanelForm } from './ZonePanelForm';
import type { Zone } from '../../../zones.api';

interface ZonePanelProps {
  /** A zone id, or `new`. Mount with `key={zoneId}`, so moving to another zone starts a fresh panel. */
  zoneId: string;
  map: MapLibreMap | null;
  /** Null until the catalogue arrives: a new zone's form defaults its kind from it, so it waits. */
  zoneKinds: ZoneKind[] | null;
  onChanged: () => Promise<void>;
  onClose: () => void;
  /** A new zone's place search matched an existing zone: open that one instead. */
  onOpenZone: (zoneId: string) => void;
  /** Inside the table's zone sheet, which frames it already: no card of its own. */
  bare?: boolean;
}

/**
 * One zone beside the Zones map: its fields, and its shape drawn on the map itself, so every other
 * zone stays in view while it is traced. Replaces the separate editor page and its own map.
 */
export function ZonePanel({ zoneId, map, zoneKinds, onChanged, onClose, onOpenZone, bare = false }: ZonePanelProps) {
  const panel = useZonePanel({ zoneId, map, onChanged, onClose });
  const { record, zone, shape, drawing } = panel;

  if (record.status === 'forbidden') return <Navigate to="/" replace />;

  return (
    <aside aria-label={zone ? zone.name : 'New zone'} className={clsx('flex min-w-0 flex-col gap-5', !bare && 'rounded-lg border border-line bg-surface p-5')}>
      <PanelHeader zone={zone} isNew={zoneId === 'new'} onClose={onClose} onSetActive={panel.setActive} />
      <RecordPending record={record} isKindsLoading={!zoneKinds} />
      {record.status === 'ready' && zoneKinds && (
        <ZonePanelForm
          zone={zone}
          zoneKinds={zoneKinds}
          error={panel.saveError}
          submitting={panel.saving}
          onSave={panel.save}
          onCancel={onClose}
          map={map}
          onPickPlace={panel.startFromPlace}
          onPickZone={onOpenZone}
          shapeControls={
            <ZoneDrawControls
              shape={shape}
              drawMode={drawing.drawMode}
              onDrawModeChange={drawing.setDrawMode}
              onRadiusChange={panel.setRadius}
              onClear={drawing.clearShape}
            />
          }
        />
      )}
    </aside>
  );
}

/** The panel before its form can show: loading (the zone, or the kinds a new zone defaults from), missing or failed. */
function RecordPending({ record, isKindsLoading }: { record: ReturnType<typeof useZonePanel>['record']; isKindsLoading: boolean }) {
  if (record.status === 'not_found')
    return <EmptyState icon={FileX} title="Zone not found" description="This zone doesn't exist, or the link is out of date." />;
  if (record.status === 'error')
    return <EmptyState icon={ShieldAlert} title="Couldn't load this zone" description="Something went wrong. Try reloading the page." />;
  if (record.status !== 'loading' && !isKindsLoading) return null;
  return (
    <SkeletonRegion label="Loading zone">
      <SkeletonFields count={4} />
      <Skeleton className="mt-8 h-8 w-32" />
    </SkeletonRegion>
  );
}

interface PanelHeaderProps {
  zone: Zone | null;
  isNew: boolean;
  onClose: () => void;
  onSetActive: (isActive: boolean) => Promise<void>;
}

function PanelHeader({ zone, isNew, onClose, onSetActive }: PanelHeaderProps) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="truncate text-lg font-semibold text-fg">{zone?.name ?? (isNew ? 'New zone' : 'Zone')}</h2>
        {zone && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Tag color={zone.isActive ? 'success' : 'default'}>{zone.isActive ? 'Active' : 'Inactive'}</Tag>
            <Popconfirm title={`${zone.isActive ? 'Deactivate' : 'Reactivate'} ${zone.name}?`} onConfirm={() => onSetActive(!zone.isActive)}>
              <Button size="small" type="link" danger={zone.isActive} className="px-0">
                {zone.isActive ? 'Deactivate' : 'Reactivate'}
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
      <Button type="text" size="small" aria-label="Close" icon={<X className="size-4" />} onClick={onClose} />
    </header>
  );
}
